import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react'
import { alertBridge } from '../utils/alertBridge'

export type AlertType = 'success' | 'error' | 'warning' | 'info'

export interface AlertMessage {
  id: string
  type: AlertType
  title?: string
  message: string
  duration?: number // milliseconds, 0 means no auto-dismiss
  isRemoving?: boolean
}

interface AlertContextType {
  alerts: AlertMessage[]
  showAlert: (
    type: AlertType,
    message: string,
    title?: string,
    duration?: number
  ) => void
  showSuccess: (message: string, title?: string) => void
  showError: (message: string, title?: string) => void
  showWarning: (message: string, title?: string) => void
  showInfo: (message: string, title?: string) => void
  removeAlert: (id: string) => void
  clearAlerts: () => void
}

const AlertContext = createContext<AlertContextType | undefined>(undefined)

export const useAlert = () => {
  const context = useContext(AlertContext)
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider')
  }
  return context
}

interface AlertProviderProps {
  children: React.ReactNode
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [alerts, setAlerts] = useState<AlertMessage[]>([])

  const showAlert = useCallback(
    (type: AlertType, message: string, title?: string, duration?: number) => {
      const id = `${Date.now()}-${Math.random()}`
      const alert: AlertMessage = {
        id,
        type,
        title,
        message,
        duration:
          duration !== undefined ? duration : type === 'error' ? 7000 : 3000,
      }

      setAlerts((prev) => {
        const updated = [...prev, alert]
        return updated
      })

      // Auto-dismiss if duration > 0
      if (alert.duration && alert.duration > 0) {
        setTimeout(() => {
          removeAlert(id)
        }, alert.duration)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const removeAlert = useCallback((id: string) => {
    // Mark alert as removing to trigger exit animation
    setAlerts((prev) =>
      prev.map((alert) =>
        alert.id === id ? { ...alert, isRemoving: true } : alert
      )
    )

    // Remove alert after animation completes
    setTimeout(() => {
      setAlerts((prev) => prev.filter((alert) => alert.id !== id))
    }, 300) // Match animation duration
  }, [])

  const clearAlerts = useCallback(() => {
    setAlerts([])
  }, [])

  const showSuccess = useCallback(
    (message: string, title?: string) => {
      showAlert('success', message, title)
    },
    [showAlert]
  )

  const showError = useCallback(
    (message: string, title?: string) => {
      showAlert('error', message, title)
    },
    [showAlert]
  )

  const showWarning = useCallback(
    (message: string, title?: string) => {
      showAlert('warning', message, title)
    },
    [showAlert]
  )

  const showInfo = useCallback(
    (message: string, title?: string) => {
      showAlert('info', message, title)
    },
    [showAlert]
  )

  // Subscribe to alert bridge for external alerts
  useEffect(() => {
    const unsubscribe = alertBridge.subscribe((type, message, title) => {
      showAlert(type, message, title)
    })
    return unsubscribe
  }, [showAlert])

  return (
    <AlertContext.Provider
      value={{
        alerts,
        showAlert,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        removeAlert,
        clearAlerts,
      }}
    >
      {children}
    </AlertContext.Provider>
  )
}
